import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Button,
  Empty,
  Input,
  Progress,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AimOutlined,
  ArrowRightOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  RobotOutlined,
  SearchOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useProfile } from '../../profile/components/ProfileContext';
import { route } from '../../../../../constants/routes';
import { JobSkillsSuggestionService } from '../../job/services/job-skills-suggestion.service';
import {
  CandidateMatchResponse,
  CandidateMatchResult,
} from '../models/candidate-match.model';
import { CandidateMatchService } from '../services/candidate-match.service';
import './CandidateMatchPage.css';

const { TextArea } = Input;
const { Paragraph, Text, Title } = Typography;

interface SuggestedSkill {
  name: string;
  kind: 'required' | 'preferred';
}

function uniqueSkills(skills: string[]): string[] {
  const unique = new Map<string, string>();
  skills.forEach((skill) => {
    const trimmed = skill.trim();
    if (trimmed) {
      unique.set(trimmed.toLowerCase(), trimmed);
    }
  });
  return Array.from(unique.values());
}

function initials(candidate: CandidateMatchResult): string {
  return `${candidate.firstname?.[0] ?? ''}${candidate.lastname?.[0] ?? ''}`.toUpperCase();
}

function fitColor(score: number): string {
  if (score >= 75) return '#16875d';
  if (score >= 45) return '#d18a00';
  return '#d04a43';
}

function fitLabel(score: number): string {
  if (score >= 75) return 'Strong skill fit';
  if (score >= 45) return 'Partial skill fit';
  return 'Limited skill fit';
}

const CandidateMatchPage: React.FC = () => {
  const { companyId } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const initialCriteria = location.state?.copilotCriteria;
  const [position, setPosition] = useState(initialCriteria?.position ?? '');
  const [description, setDescription] = useState(initialCriteria?.description ?? '');
  const [skills, setSkills] = useState<string[]>(
    uniqueSkills(initialCriteria?.skills ?? [])
  );
  const [suggestedSkills, setSuggestedSkills] = useState<SuggestedSkill[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [result, setResult] = useState<CandidateMatchResponse | null>(null);
  const [criteriaExpanded, setCriteriaExpanded] = useState(true);

  useEffect(() => {
    if (!initialCriteria) return;
    setPosition(initialCriteria.position ?? '');
    setDescription(initialCriteria.description ?? '');
    setSkills(uniqueSkills(initialCriteria.skills ?? []));
    setCriteriaExpanded(true);
  }, [initialCriteria, location.key]);

  const canSearch = Boolean(companyId && position.trim() && skills.length);
  const canSuggest = Boolean(position.trim() && description.trim());

  const addSuggestedSkill = (name: string) => {
    setSkills((current) => uniqueSkills([...current, name]));
    setSuggestedSkills((current) =>
      current.filter((skill) => skill.name !== name)
    );
  };

  const handleSuggestSkills = async () => {
    if (!canSuggest) {
      message.info('Add a position and description before suggesting skills.');
      return;
    }
    setSuggesting(true);
    setSuggestionError(null);
    try {
      const response = await JobSkillsSuggestionService.Suggest({
        jobTitle: position.trim(),
        description,
        existingSkills: skills,
      });
      const suggestions: SuggestedSkill[] = [
        ...(response.requiredSkills ?? []).map((skill) => ({
          name: skill.skill,
          kind: 'required' as const,
        })),
        ...(response.niceToHaveSkills ?? []).map((skill) => ({
          name: skill.skill,
          kind: 'preferred' as const,
        })),
      ];
      setSuggestedSkills(
        suggestions.filter(
          (suggestion) =>
            !skills.some(
              (skill) => skill.toLowerCase() === suggestion.name.toLowerCase()
            )
        )
      );
      if (!suggestions.length) {
        message.info('No additional skills were suggested.');
      }
    } catch (error) {
      console.error('Candidate Match skill suggestion failed:', error);
      setSuggestionError(
        'Could not suggest skills from this description. You can still enter them manually.'
      );
    } finally {
      setSuggesting(false);
    }
  };

  const handleSearch = async () => {
    if (!companyId || !canSearch) {
      message.info('Add a position and at least one skill.');
      return;
    }
    setSearching(true);
    setSearchError(null);
    try {
      const response = await CandidateMatchService.Search(companyId, {
        position: position.trim(),
        description: description.trim() || undefined,
        skills: uniqueSkills(skills),
        limit: 25,
      });
      setResult(response);
      setCriteriaExpanded(false);
    } catch (error) {
      console.error('Candidate Match search failed:', error);
      setSearchError(
        'Candidate Match is temporarily unavailable. Review the criteria and try again.'
      );
    } finally {
      setSearching(false);
    }
  };

  const columns = useMemo<ColumnsType<CandidateMatchResult>>(
    () => [
      {
        title: 'Candidate',
        key: 'candidate',
        width: 230,
        render: (_, candidate) => (
          <div className="candidate-match-person">
            <Avatar>{initials(candidate)}</Avatar>
            <div>
              <strong>
                {candidate.firstname} {candidate.lastname}
              </strong>
              <span>{candidate.email}</span>
            </div>
          </div>
        ),
      },
      {
        title: 'Skill fit',
        dataIndex: 'skillFitScore',
        key: 'skillFitScore',
        width: 165,
        sorter: (first, second) => first.skillFitScore - second.skillFitScore,
        render: (score: number, candidate) => (
          <div className="candidate-match-fit">
            <div>
              <strong>{score}%</strong>
              <span>
                {candidate.matchedSkillCount}/{candidate.totalSkillCount} skills
              </span>
            </div>
            <Progress
              percent={score}
              showInfo={false}
              size="small"
              strokeColor={fitColor(score)}
            />
            <small>{fitLabel(score)}</small>
          </div>
        ),
      },
      {
        title: 'Matching evidence',
        dataIndex: 'matchedSkills',
        key: 'matchedSkills',
        width: 250,
        render: (matchedSkills: string[], candidate) => (
          <div className="candidate-match-evidence">
            <div>
              {matchedSkills.slice(0, 4).map((skill) => (
                <Tag key={skill} icon={<CheckCircleOutlined />} color="success">
                  {skill}
                </Tag>
              ))}
              {matchedSkills.length > 4 ? (
                <Tag>+{matchedSkills.length - 4}</Tag>
              ) : null}
            </div>
            {candidate.missingSkills.length ? (
              <Tooltip title={candidate.missingSkills.join(', ')}>
                <span>
                  <CloseCircleOutlined /> {candidate.missingSkills.length} skill
                  {candidate.missingSkills.length === 1 ? '' : 's'} not found
                </span>
              </Tooltip>
            ) : null}
          </div>
        ),
      },
      {
        title: 'Position signal',
        key: 'position',
        width: 250,
        render: (_, candidate) =>
          candidate.inferredPosition ? (
            <div className="candidate-match-position">
              <strong>{candidate.inferredPosition}</strong>
              <span>{candidate.positionConfidence}% resume confidence</span>
              {candidate.positionRationale ? (
                <Tooltip title={candidate.positionRationale}>
                  <small>{candidate.positionRationale}</small>
                </Tooltip>
              ) : null}
            </div>
          ) : (
            <Text type="secondary" className="candidate-match-no-signal">
              No matching position signal yet
            </Text>
          ),
      },
      {
        title: 'Action',
        key: 'action',
        width: 125,
        fixed: 'right',
        render: (_, candidate) => (
          <Button
            type="link"
            icon={<ArrowRightOutlined />}
            iconPosition="end"
            onClick={() =>
              navigate(route(`/candidate-profile/${candidate.candidateId}`), {
                state: {
                  candidateName:
                    `${candidate.firstname} ${candidate.lastname}`.trim(),
                  fromCandidateMatch: true,
                },
              })
            }
          >
            View profile
          </Button>
        ),
      },
    ],
    [navigate]
  );

  return (
    <main className="candidate-match-page">
      <header className="candidate-match-hero">
        <div>
          <span className="candidate-match-eyebrow">Talent intelligence</span>
          <Title level={1}>Candidate Match</Title>
          <Paragraph>
            Describe the position, select the skills that matter, and find the
            strongest evidence already in your talent pool.
          </Paragraph>
        </div>
        <span className="candidate-match-hero__icon">
          <AimOutlined aria-hidden />
        </span>
      </header>

      <section
        className={`candidate-match-builder${
          criteriaExpanded ? '' : ' candidate-match-builder--collapsed'
        }`}
        aria-labelledby="match-builder-title"
      >
        <div className="candidate-match-builder__header">
          <div>
            <span>New search</span>
            <h2 id="match-builder-title">
              What does a strong candidate look like?
            </h2>
            <p>
              AI can suggest criteria from the description, but you control the
              final skills used for ranking.
            </p>
          </div>
          <div className="candidate-match-builder__tools">
            <Tag icon={<RobotOutlined />}>AI-assisted</Tag>
            {!criteriaExpanded ? (
              <Button type="link" onClick={() => setCriteriaExpanded(true)}>
                Edit criteria
              </Button>
            ) : null}
          </div>
        </div>

        {criteriaExpanded ? (
          <div className="candidate-match-form">
            <div className="candidate-match-field">
              <label htmlFor="candidate-match-position">Position</label>
              <Input
                id="candidate-match-position"
                size="large"
                prefix={<AimOutlined />}
                value={position}
                maxLength={255}
                placeholder="e.g. Senior Backend Engineer"
                onChange={(event) => setPosition(event.target.value)}
              />
            </div>
            <div className="candidate-match-field">
              <div className="candidate-match-field__label-row">
                <label htmlFor="candidate-match-skills">
                  Skills used for ranking
                </label>
                <Button
                  type="link"
                  icon={<BulbOutlined />}
                  loading={suggesting}
                  disabled={!canSuggest}
                  onClick={() => void handleSuggestSkills()}
                >
                  Suggest from description
                </Button>
              </div>
              <Select
                id="candidate-match-skills"
                mode="tags"
                size="large"
                value={skills}
                tokenSeparators={[',']}
                placeholder="Type a skill and press Enter"
                onChange={(values) => setSkills(uniqueSkills(values))}
                options={[]}
              />
              <span className="candidate-match-field__hint">
                Add the capabilities that must be visible on a candidate
                profile.
              </span>
            </div>
            <div className="candidate-match-field candidate-match-field--full">
              <label htmlFor="candidate-match-description">
                Role description
              </label>
              <TextArea
                id="candidate-match-description"
                value={description}
                rows={5}
                maxLength={50000}
                showCount
                placeholder="Describe responsibilities, architecture, domain, and what success looks like…"
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>

            {suggestionError ? (
              <Alert
                className="candidate-match-form__wide"
                type="warning"
                showIcon
                message={suggestionError}
              />
            ) : null}

            {suggestedSkills.length ? (
              <div className="candidate-match-suggestions candidate-match-form__wide">
                <div>
                  <RobotOutlined />
                  <span>
                    Suggested criteria — click a skill to add it to the search
                  </span>
                </div>
                <div>
                  {suggestedSkills.map((skill) => (
                    <button
                      type="button"
                      key={`${skill.kind}-${skill.name}`}
                      onClick={() => addSuggestedSkill(skill.name)}
                    >
                      <span>
                        {skill.kind === 'required' ? 'Required' : 'Preferred'}
                      </span>
                      {skill.name}
                      <span aria-hidden>+</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="candidate-match-form__actions">
              <div>
                <TeamOutlined />
                Scores use verified skills. Position confidence is supporting
                résumé evidence.
              </div>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                loading={searching}
                disabled={!canSearch}
                onClick={() => void handleSearch()}
              >
                Find matching candidates
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      {searchError ? (
        <Alert
          className="candidate-match-error"
          type="error"
          showIcon
          message={searchError}
        />
      ) : null}

      {searching ? (
        <section
          className="candidate-match-loading"
          aria-label="Searching candidates"
        >
          <Spin size="large" />
          <div>
            <strong>Searching your talent pool</strong>
            <span>Comparing candidate skills and inferred positions…</span>
          </div>
        </section>
      ) : null}

      {!searching && result ? (
        <section
          className="candidate-match-results"
          aria-labelledby="match-results-title"
        >
          <div className="candidate-match-results__header">
            <div>
              <span className="candidate-match-eyebrow">Search results</span>
              <h2 id="match-results-title">
                {result.totalElements} matching candidate
                {result.totalElements === 1 ? '' : 's'}
              </h2>
              <p>
                Ranked by selected skill coverage. Position confidence is shown
                separately as supporting resume evidence.
              </p>
            </div>
            <div className="candidate-match-results__criteria">
              <Tag icon={<AimOutlined />}>{result.position}</Tag>
              <Tag>{result.resolvedSkills.length} resolved skills</Tag>
            </div>
          </div>

          {result.unresolvedSkills.length ? (
            <Alert
              className="candidate-match-unresolved"
              type="warning"
              showIcon
              message={`${result.unresolvedSkills.length} skill ${
                result.unresolvedSkills.length === 1 ? 'was' : 'were'
              } not found in candidate profiles`}
              description={result.unresolvedSkills.join(', ')}
            />
          ) : null}

          {result.candidates.length ? (
            <Table
              className="candidate-match-table"
              rowKey="candidateId"
              columns={columns}
              dataSource={result.candidates}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              scroll={{ x: 1120 }}
            />
          ) : (
            <Empty
              className="candidate-match-empty"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <strong>No matching profiles yet</strong>
                  <span>
                    Try broader skills or update candidate profiles with their
                    latest résumé.
                  </span>
                </div>
              }
            />
          )}
        </section>
      ) : null}
    </main>
  );
};

export default CandidateMatchPage;
